import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Modal, Popconfirm, message } from "antd";
import { CheckCircleFilled, CloseCircleFilled, EditOutlined, PoweroffOutlined } from "@ant-design/icons";
import { Amap, CircleMarker } from "@amap/amap-react";
import moment from "moment";
import GatewayApi from "../../../../../../api/GatewayApi";
import StringUtils from "../../../../../../util/StringUtils";
import { DeviceStatusUtils } from "../../../../../../components/ui-components/device-status/DeviceStatus";
import UpdateGatewayInformation from "./update-gateway-information/UpdateGatewayInformation";
import { GatewayInformationWrapper, GatewayLocationMapWrapper, InfoColumn, InfoGroup, InfoGroupContent, InfoGroupSeparator, InfoGroupTitle, InfoItem, InfoLabel, InfoValue, ProductModelPicture } from "./GatewayInformation.styled";

interface GatewayInformationProps {
    gatewayView: any
}

export const GatewayInformation: React.FC<GatewayInformationProps> = (props)=> {
    // params
    let latestHeartBeatMetric = props.gatewayView.latestHeartBeatMetric;
    let mapCenter = latestHeartBeatMetric == null ? [116, 39]: [latestHeartBeatMetric.longitude, latestHeartBeatMetric.latitude];
    let [ searchParams ] = useSearchParams();

    // api
    const navigate = useNavigate();
    const [messageApi, contextHolder] = message.useMessage();
    let gatewayApi = new GatewayApi();

    // state
    let [openUpdateGatewayInformationModal, setOpenUpdateGatewayInformationModal] = useState(false);
    let [gatewayView, setGatewayView] = useState(props.gatewayView);

    // events
    let refreshGatewayView = async ()=> {
        let newGatewayView = (await gatewayApi.getGatewayViewByGatewayId(gatewayView.gateway.id)).data.data;
        setGatewayView(newGatewayView);
    };

    let onEditGatewayInformation = ()=> {
        setOpenUpdateGatewayInformationModal(true);
    };

    let onReboot = async ()=> {
        let responseData = (await gatewayApi.invokeService(gatewayView.gateway.id, 'reboot', {})).data.data;
        messageApi.open({
            type: 'success',
            content: '发送命令成功',
        });
    };

    // utils
    let formatDateTime = (timestamp: number | string)=> {
        timestamp = Number(timestamp);
        return moment(timestamp).format('YYYY/MM/DD HH:mm:ss');
    }

    // render
    return (
        <GatewayInformationWrapper>
            {contextHolder}
            <InfoGroup>
                <InfoGroupTitle>网关信息 <Button type='link' onClick={onEditGatewayInformation}><EditOutlined />编辑</Button></InfoGroupTitle>
                <InfoGroupContent>
                    <InfoColumn>
                        <ProductModelPicture style={{
                            backgroundImage: `url(/static/images/product-model/${gatewayView.gateway.productModel}/model.jpg)`
                        }} />

                        <InfoItem>
                            <InfoLabel></InfoLabel>
                            <InfoValue>
                            <Popconfirm
                                    title="重启网关"
                                    description={ <div style={{paddingTop: "10px", paddingBottom: '10px'}}>确认重启该网关么?</div> }
                                    onConfirm={()=> onReboot()}
                                    okText="确定"
                                    cancelText="取消"
                                > 
                                {searchParams.get('reboot') && <Button type='link' disabled={gatewayView.node.status == 'OFFLINE'} style={{marginTop: '10px'}}><PoweroffOutlined />重启设备</Button> }
                            </Popconfirm>
                            </InfoValue>
                        </InfoItem>
                    </InfoColumn>

                    <InfoColumn>
                        <InfoItem>
                            <InfoLabel>网关状态</InfoLabel>
                            {
                                gatewayView.node.status == 'ONLINE'
                                ? <InfoValue><CheckCircleFilled style={{color: 'green', marginRight: '10px'}}/>在线</InfoValue>
                                : <InfoValue><CloseCircleFilled style={{color: 'grey', marginRight: '10px'}} />离线</InfoValue>
                            }
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>信号强度</InfoLabel>
                            <InfoValue>{gatewayView.latestHeartBeatMetric.rssi}dbm</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>最后在线时间 (yyyy/MM/dd)</InfoLabel>
                            <InfoValue>{formatDateTime(gatewayView.latestHeartBeatMetric.timestamp)}</InfoValue>
                        </InfoItem>
                    </InfoColumn>

                    <InfoColumn>
                        <InfoItem>
                            <InfoLabel>网关名称</InfoLabel>
                            <InfoValue>{gatewayView.gateway.name}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>网关编号</InfoLabel>
                            <InfoValue>{gatewayView.gateway.gatewayNo}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>产品型号</InfoLabel>
                            <InfoValue>{gatewayView.product.modelName}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>备注</InfoLabel>
                            <InfoValue>{StringUtils.emptyToSlash(gatewayView.gateway.remarks)}</InfoValue>
                        </InfoItem>
                    </InfoColumn>

                    <InfoColumn>
                        <InfoItem>
                            <InfoLabel>序列号 SN</InfoLabel>
                            <InfoValue>{gatewayView.node.sn}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>固件版本</InfoLabel>
                            <InfoValue>Ver {gatewayView.node.firmwareVersion}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>IMEI</InfoLabel>
                            <InfoValue>{gatewayView.node.imei}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>ICCID</InfoLabel>
                            <InfoValue>{gatewayView.node.iccid}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>运营商</InfoLabel>
                            <InfoValue>{gatewayView.node.oper}</InfoValue>
                        </InfoItem>
                    </InfoColumn>
                </InfoGroupContent>
            </InfoGroup>
            
            <InfoGroupSeparator />

            <InfoGroup>
                <InfoGroupTitle>网关定位</InfoGroupTitle>
                <InfoGroupContent>
                    <InfoColumn style={{width: 'auto', marginRight: '50px'}}>
                        <GatewayLocationMapWrapper>
                            <Amap
                                mapStyle='amap://styles/dark'
                                zoom={4}
                                center={mapCenter}>
                                
                                {
                                    latestHeartBeatMetric && (
                                        <CircleMarker
                                            center={mapCenter} 
                                            radius={6} 
                                            fillColor={DeviceStatusUtils.getDeviceMarkerColor(gatewayView.node.status)}
                                            fillOpacity={0.8} 
                                            strokeWeight={0}
                                            cursor="pointer" />
                                    )
                                }
                            </Amap>
                        </GatewayLocationMapWrapper>
                    </InfoColumn>
                    <InfoColumn>
                        <InfoItem>
                            <InfoLabel>定位方式</InfoLabel>
                            <InfoValue>{latestHeartBeatMetric?.altitude == null ? '基站定位': 'GNSS'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>最后位置时间</InfoLabel>
                            <InfoValue>{latestHeartBeatMetric==null ? '未知' : formatDateTime(latestHeartBeatMetric.timestamp)}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>经度</InfoLabel>
                            <InfoValue>{latestHeartBeatMetric?.longitude?.toFixed(6) ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>纬度</InfoLabel>
                            <InfoValue>{latestHeartBeatMetric?.latitude?.toFixed(6) ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>高度</InfoLabel>
                            <InfoValue>{latestHeartBeatMetric?.altitude?.toFixed(6) ?? 'N/A'}</InfoValue>
                        </InfoItem>
                    </InfoColumn>
                    <InfoColumn>
                        <InfoItem>
                            <InfoLabel>速度 (km/h)</InfoLabel>
                            <InfoValue>{latestHeartBeatMetric?.speed_km?.toFixed(0) ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>方向</InfoLabel>
                            <InfoValue>{latestHeartBeatMetric?.course?.toFixed(2) ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>卫星数量</InfoLabel>
                            <InfoValue>{latestHeartBeatMetric?.satelites?.toFixed(2) ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>LAC</InfoLabel>
                            <InfoValue>{latestHeartBeatMetric?.lac ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>CI</InfoLabel>
                            <InfoValue>{latestHeartBeatMetric?.ci ?? 'N/A'}</InfoValue>
                        </InfoItem>
                    </InfoColumn>
                </InfoGroupContent>
            </InfoGroup>

            <Modal open={openUpdateGatewayInformationModal} 
                onClose={()=> setOpenUpdateGatewayInformationModal(false)} 
                footer={null} 
                closable={false}
                destroyOnClose={true}
                width={1000}
                >
                <UpdateGatewayInformation
                    gatewayView={gatewayView}
                    onClose={()=> setOpenUpdateGatewayInformationModal(false)} 
                    onComplete={()=> refreshGatewayView()}/>
            </Modal>
        </GatewayInformationWrapper>
    )
}

export default GatewayInformation;
